import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { updateSessionActivity } from "../lib/auth";

// Check session validity every minute
const CHECK_INTERVAL_MS = 60 * 1000;

// Activity events to track
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Hook to monitor session validity and track user activity
 * - Checks session expiry and inactivity periodically
 * - Updates activity timestamp on user interactions
 * - Redirects to login when session expires with appropriate error message
 * 
 * Conforme a Specifiche_Integrazione_TDH.md:
 * - TOKEN_EXPIRED: "La sessione è scaduta. Effettua nuovamente l'accesso."
 * - SESSION_TIMEOUT: "Sessione scaduta per inattività. Effettua nuovamente l'accesso."
 * 
 * Nota: Il Portale Operatori non supporta il refresh del token.
 * Quando il token scade, l'utente deve ri-autenticarsi dal Portale.
 */
export function useSessionMonitor() {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    checkSessionValidity, 
    updateActivity, 
    error, 
    errorCode,
  } = useAuthStore();
  const wasAuthenticatedRef = useRef(isAuthenticated);

  // Handle session check
  const handleSessionCheck = useCallback(() => {
    if (!isAuthenticated) return;
    
    const isDev = import.meta.env.DEV;
    const isValid = checkSessionValidity();
    
    // checkSessionValidity() calls logout(errorCode) which sets the error
    // Redirect happens in the useEffect below when isAuthenticated becomes false
    if (!isValid && isDev) {
      console.log("[SessionMonitor] Session invalid, redirect will happen via auth state change");
    }
  }, [isAuthenticated, checkSessionValidity]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;
    
    updateActivity();
    updateSessionActivity();
  }, [isAuthenticated, updateActivity]);

  // Redirect to login when authentication state changes from true to false
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      if (import.meta.env.DEV) {
        console.log("[SessionMonitor] Auth state changed to false, redirecting to login", { errorCode });
      }
      navigate("/login", { replace: true });
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, navigate, errorCode]);

  // Set up session check interval
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial check
    handleSessionCheck();

    // Periodic checks
    const interval = setInterval(handleSessionCheck, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated, handleSessionCheck]);

  // Set up activity tracking
  useEffect(() => {
    if (!isAuthenticated) return;

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Clean up
    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, handleActivity]);

  // Return error info for display
  return { 
    sessionError: error,
    sessionErrorCode: errorCode,
  };
}
